/*
 * Miroir web de `packages/domain/src/content/grading-scale.ts` : `apps/web` ne
 * résout pas `@naia-app/domain`. La forme persistée est le contrat — la
 * modifier ici sans le domaine fabrique des échelles que l'API relit autrement.
 */
export interface GradingScaleLevel {
  code: string;
  label: string;
  rank: number;
  level4: number | null;
}

export interface GradingScale {
  id: string;
  label: string;
  levels: GradingScaleLevel[];
  notEvaluated: { code: string; label: string } | null;
}

export type SectionGrading = { mode: 'numeric' } | { mode: 'scale'; scaleId: string; scale: GradingScale };

export const buildDefaultScale = (id: string): GradingScale => ({
  id,
  label: 'Compétences',
  levels: [
    { code: 'A', label: 'Acquis', rank: 7, level4: 4 },
    { code: 'A-', label: 'Acquis (fragile)', rank: 6, level4: 3 },
    { code: 'ECA+', label: 'En cours d’acquisition +', rank: 5, level4: 3 },
    { code: 'ECA', label: 'En cours d’acquisition', rank: 4, level4: 2 },
    { code: 'ECA-', label: 'En cours d’acquisition -', rank: 3, level4: 2 },
    { code: 'NA+', label: 'Non acquis +', rank: 2, level4: 1 },
    { code: 'NA', label: 'Non acquis', rank: 1, level4: 1 },
  ],
  notEvaluated: { code: 'NE', label: 'Non évalué' },
});

export const withRanks = (levels: GradingScaleLevel[]): GradingScaleLevel[] =>
  levels.map((l, i) => ({ ...l, rank: levels.length - i }));

export function gradingOf(content: unknown): SectionGrading {
  const g = (content as { grading?: SectionGrading } | null)?.grading;
  return g && g.mode === 'scale' && g.scale && g.scale.id === g.scaleId ? g : { mode: 'numeric' };
}

export function scalesOfSections(sections: readonly { type: string; content?: unknown }[]): GradingScale[] {
  const byId = new Map<string, GradingScale>();
  for (const s of sections) {
    if (s.type !== 'qcm' && s.type !== 'question_with_answer') continue;
    const g = gradingOf(s.content);
    if (g.mode === 'scale' && !byId.has(g.scaleId)) byId.set(g.scaleId, g.scale);
  }
  return [...byId.values()];
}

export interface GradedItem {
  score: number;
  max: number;
  unread?: boolean;
  letter?: string;
  scaleId?: string;
}

export const isLetterItem = (it: GradedItem): boolean => it.scaleId !== undefined;

export const gradeLabel = (it: GradedItem): string => (isLetterItem(it) ? (it.letter ?? '—') : `${it.score}/${it.max}`);

export function numericTotals(items: readonly GradedItem[]): { totalScore: number; totalMax: number } {
  return items.reduce(
    (acc, it) => (it.unread || isLetterItem(it) ? acc : { totalScore: acc.totalScore + it.score, totalMax: acc.totalMax + it.max }),
    { totalScore: 0, totalMax: 0 },
  );
}
